/**
 * ==============================================================
 * SPORT STUFF
 * ==============================================================
 */
const SPORTS = {
  SNOW: 'SNOW',
  BIKE: 'BIKE',
};

const getSportName = (sport) => {
  switch (sport) {
    case SPORTS.SNOW:
      return 'Ski / Snowboard';
    case SPORTS.BIKE:
      return 'Bike';
    default:
      return 'Unknown';
  }
};

const PRIMARY_ENTITY_OPTIONS = {
  FEATURES: 'FEATURES',
  TRAILS: 'TRAILS',
  LIFTS: 'LIFTS',
};

/**
 * ==============================================================
 * COLOR STUFF
 * ==============================================================
 */
 const COLORS_COMMON = {
  LIGHT_GRAY: '#ACA7C1',
  LIGHT_GRAY_TRANSLUCENT: 'rgba(172, 167, 193, 0.4)',
  DARK_GRAY: '#948C9C', // DIS-SELECTED
  OFF_WHITE: '#fbfbfb',
  WHITE: '#ffffff',
  TRANSPARENT: 'rgba(0,0,0,0)',
  LIST_BACKGROUND: '#e5ebf3',
  GRAY_1: '#f0f0f0',
  LIST_BACKGROUND_DARK: '#909090',
  GREEN: '#3FC380',
  RED_ALERT: '#E26556',
  FREESTYLE_ORANGE: '#ffa43d',
  ORANGE_TRANSLUCENT: 'rgba(255, 164, 61, 0.45)',
  GREEN_TRAIL: '#0f0',
  BLUE: '#3252a8',
  BLUE_TRAIL: '#00f',
  PURPLE: '#6f5499',
  BLACK: '#000000',
  RED_TRAIL: '#f00',
  HOLD_YELLOW: '#fcc515'
};

const COLORS_SNOW = {
  ...COLORS_COMMON,
  PRIMARY: '#6B8CF9',
  SECONDARY: '#5F6CCB',
  DARK: '#000044',
};

const COLORS_BIKE = {
  ...COLORS_COMMON,
  PRIMARY: '#50A162',
  SECONDARY: '#106022',
  DARK: '#002200',
};

const COLORS = (sport) => {
  switch (sport.toUpperCase()) {
    case SPORTS.BIKE:
      return COLORS_BIKE;
    case SPORTS.SNOW:
    default:
      return COLORS_SNOW;
  }
};

const COLORS_LIFT_STATUS = {
  'Open': COLORS_COMMON.GREEN,
  'Closed': COLORS_COMMON.RED_ALERT,
  'Scheduled': COLORS_COMMON.BLUE,
  'Hold': COLORS_COMMON.HOLD_YELLOW,
  'Wind hold': COLORS_COMMON.HOLD_YELLOW,
  'Weather delay': COLORS_COMMON.HOLD_YELLOW,
};

const getColorForLiftStatus = (status) => {
  return COLORS_LIFT_STATUS[status] || COLORS_COMMON.DARK_GRAY;
};

/**
 * ==============================================================
 * FEATURE STUFF
 * ==============================================================
 */
 const FEATURE_STATUSES = {
  OPEN: 1,
  CLOSED: 2,
  REMOVED: -1,
  ARCHIVED: -2,
};

 const FEATURE_TYPES_SNOW = {
  BOX: 0,
  RAIL: 1,
  JUMP: 2,
  SNOW_FEATURE: 3,
  JIB: 4,
};

const FEATURE_TYPES_BIKE = {
  ROCK_GARDEN: 0,
  DROP: 1,
  JUMP: 2,
  BERM: 3,
  HIP: 8,
  ROLLER: 9,
  ROCK_FACE: 4,
  BRIDGE: 5,
  SKINNY: 6,
  OTHER: 7,
};

const FEATURE_TYPES = (sport) => {
  switch (sport.toUpperCase()) {
    case SPORTS.BIKE:
      return FEATURE_TYPES_BIKE;
    case SPORTS.SNOW:
    default:
      return FEATURE_TYPES_SNOW;
  }
};

const getFeatureTypeNameFromFeatureTypeEnum = (sport, type) => {
  if (sport === SPORTS.BIKE) {
    switch (Number(type)) {
      case FEATURE_TYPES_BIKE.ROCK_GARDEN:
        return 'Rock Garden';
      case FEATURE_TYPES_BIKE.DROP:
        return 'Drop';
      case FEATURE_TYPES_BIKE.JUMP:
        return 'Jump';
      case FEATURE_TYPES_BIKE.BERM:
        return 'Berm';
      case FEATURE_TYPES_BIKE.HIP:
        return 'Hip';
      case FEATURE_TYPES_BIKE.ROLLER:
        return 'Roller';
      case FEATURE_TYPES_BIKE.ROCK_FACE:
        return 'Rock Face';
      case FEATURE_TYPES_BIKE.BRIDGE:
        return 'Bridge';
      case FEATURE_TYPES_BIKE.SKINNY:
        return 'Skinny';
      case FEATURE_TYPES_BIKE.OTHER:
        return 'Other';
      default:
        return '?';
    }
  } else {
    switch (Number(type)) {
      case FEATURE_TYPES_SNOW.BOX:
        return 'Box';
      case FEATURE_TYPES_SNOW.RAIL:
        return 'Rail';
      case FEATURE_TYPES_SNOW.JUMP:
        return 'Jump';
      case FEATURE_TYPES_SNOW.SNOW_FEATURE:
        return 'Snow Feature';
      case FEATURE_TYPES_SNOW.JIB:
        return 'Jib';
      default:
        return '?';
    }
  }
};

const getFeatureStatusNameFromStatusEnum = (status) => {
  switch (Number(status)) {
    case -2: // archived
      return 'Archived';
    case -1: // down
      return 'Removed';
    case 1: // up
      return 'Up & Open';
    case 2: // up and closed
      return 'Up & Closed';
    default: // 0 is a valid legacy "down" status — not a removal
      return 'Down';
  }
};

/**
 * ==============================================================
 * TRAIL STUFF
 * ==============================================================
 */
 const TRAIL_STATUSES = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  SCHEDULED: 'SCHEDULED',
  HOLD: 'HOLD',
  NOT_REPORTED: 'NOT_REPORTED',
  ARCHIVED: 'ARCHIVED',
};

const getTrailStatusName = (status) => {
  switch (status) {
    case TRAIL_STATUSES.OPEN:
      return 'Open';
    case TRAIL_STATUSES.SCHEDULED:
      return 'Scheduled';
    case TRAIL_STATUSES.HOLD:
      return 'Hold';
    case TRAIL_STATUSES.NOT_REPORTED:
      return 'Unknown';
    case TRAIL_STATUSES.CLOSED:
    case TRAIL_STATUSES.ARCHIVED:
    default:
      return 'Closed';
  }
};

const COLORS_TRAIL_STATUS = {
  [TRAIL_STATUSES.OPEN]: COLORS_COMMON.GREEN,
  [TRAIL_STATUSES.CLOSED]: COLORS_COMMON.RED_ALERT,
  [TRAIL_STATUSES.SCHEDULED]: COLORS_COMMON.BLUE,
  [TRAIL_STATUSES.HOLD]: COLORS_COMMON.HOLD_YELLOW,
  [TRAIL_STATUSES.NOT_REPORTED]: COLORS_COMMON.DARK_GRAY,
  [TRAIL_STATUSES.ARCHIVED]: '#222',
};

const getColorForTrailStatus = (status) => {
  return COLORS_TRAIL_STATUS[status] || COLORS_COMMON.DARK_GRAY;
};

/**
 * ==============================================================
 * FORM STUFF
 * ==============================================================
 */
const TOP_LEVEL_FORM_TYPES = {
  FORMS: 'FORMS',
  TRAIL_ISSUE_FORMS: 'TRAIL_ISSUE_FORMS',
};

/**
 * ==============================================================
 * LOG STUFF
 * ==============================================================
 */
const MAINTENANCE_ACTIONS = {
  OPEN: 'Open',
  CLOSE: 'Close',
}

/**
 * ==============================================================
 * SCREEN STUFF
 * ==============================================================
 */
 const size = {
  mobileS: '320px',
  mobileM: '375px',
  mobileL: '425px',
  tablet: '768px',
  laptop: '1024px',
  laptopL: '1440px',
  desktop: '2560px'
}

const DEVICE_SIZE = {
  mobileS: `(max-width: ${size.mobileS})`,
  mobileM: `(max-width: ${size.mobileM})`,
  mobileL: `(max-width: ${size.mobileL})`,
  tablet: `(max-width: ${size.tablet})`,
  laptop: `(max-width: ${size.laptop})`,
  laptopL: `(max-width: ${size.laptopL})`,
  desktop: `(max-width: ${size.desktop})`,
  desktopL: `(max-width: ${size.desktop})`
};

const SIDE_MENU_HIDDEN_WIDTH = 768;

/**
 * ==============================================================
 * MISC
 * ==============================================================
 */
const UNITS = {
  IMPERIAL: 'imperial',
  METRIC: 'metric',
};

const WIDGET_MAP_STYLES = {
  VECTOR: "VECTOR",
  SATELLITE: "SATELLITE",
};

const CREATE_OR_UPDATE = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
};


// eslint-disable-next-line import/no-anonymous-default-export
export default {
  // sport stuff
  SPORTS,
  getSportName,
  PRIMARY_ENTITY_OPTIONS,
  // color stuff
  COLORS_SNOW,
  COLORS_BIKE,
  COLORS,
  getColorForLiftStatus,
  getColorForTrailStatus,
  // feature stuff
  FEATURE_STATUSES,
  getFeatureStatusNameFromStatusEnum,
  getFeatureTypeNameFromFeatureTypeEnum,
  FEATURE_TYPES_SNOW,
  FEATURE_TYPES_BIKE,
  FEATURE_TYPES,
  // trail stuff
  TRAIL_STATUSES,
  getTrailStatusName,
  // form stuff
  TOP_LEVEL_FORM_TYPES,
  // log stuff
  MAINTENANCE_ACTIONS,
  // screen stuff
  DEVICE_SIZE,
  SIDE_MENU_HIDDEN_WIDTH,
  // misc
  UNITS,
  WIDGET_MAP_STYLES,
  CREATE_OR_UPDATE,
};
